<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useClientStore } from '../stores/clients'
import type { Client } from '../types'
import { formatPhone, unformatPhone } from '../utils/phone'
import { UiModal, UiButton, UiInput, UiCard, UiAlert, UiLoader } from '../components/ui'

const clientStore = useClientStore()

const clients = computed(() => clientStore.clients)
const isLoading = computed(() => clientStore.isLoading)
const error = computed(() => clientStore.error)

const filters = ref({ name: '', email: '', phone: '' })

const showModal = ref(false)
const isEditing = ref(false)
const form = ref<Partial<Client>>({ name: '', email: '', phone: '' })
const formErrors = ref<{ name?: string; email?: string }>({})
const saving = ref(false)
const deleteConfirmId = ref<string | null>(null)

const filteredClients = computed(() => {
  let result = clients.value
  if (filters.value.name) {
    const q = filters.value.name.toLowerCase()
    result = result.filter(c => c.name.toLowerCase().includes(q))
  }
  if (filters.value.email) {
    const q = filters.value.email.toLowerCase()
    result = result.filter(c => c.email.toLowerCase().includes(q))
  }
  if (filters.value.phone) {
    const q = unformatPhone(filters.value.phone)
    result = result.filter(c => unformatPhone(c.phone).includes(q))
  }
  return result
})

function clearFilters() {
  filters.value = { name: '', email: '', phone: '' }
}

watch(() => form.value.phone, (val) => {
  if (val !== undefined) {
    const digits = unformatPhone(val)
    form.value.phone = formatPhone(digits)
  }
})

onMounted(() => {
  clientStore.fetchClients()
})

function openNew() {
  isEditing.value = false
  form.value = { name: '', email: '', phone: '' }
  formErrors.value = {}
  showModal.value = true
}

function openEdit(client: Client) {
  isEditing.value = true
  form.value = { ...client }
  formErrors.value = {}
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  formErrors.value = {}
}

function validate(): boolean {
  const errors: { name?: string; email?: string } = {}
  if (!form.value.name?.trim()) errors.name = 'Nome é obrigatório'
  if (form.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    errors.email = 'Email inválido'
  }
  formErrors.value = errors
  return Object.keys(errors).length === 0
}

async function handleSave() {
  if (!validate()) return
  saving.value = true
  if (isEditing.value && form.value.id) {
    await clientStore.updateClient(form.value as Client)
  } else {
    await clientStore.addClient(form.value as Omit<Client, 'id'>)
  }
  saving.value = false
  if (!clientStore.error) {
    closeModal()
  }
}

async function confirmDelete(id: string) {
  deleteConfirmId.value = id
}

async function handleDelete() {
  if (!deleteConfirmId.value) return
  await clientStore.deleteClient(deleteConfirmId.value)
  deleteConfirmId.value = null
}
</script>

<template>
  <div class="page-container">
    <div class="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-xl font-bold text-surface-800 dark:text-surface-100 sm:text-3xl">Clientes</h1>
        <p class="mt-1 text-xs text-surface-500 dark:text-surface-400 sm:text-sm">Gerencie seus clientes cadastrados</p>
      </div>
      <UiButton @click="openNew">
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        <span class="hidden sm:inline">Novo Cliente</span>
        <span class="sm:hidden">Novo</span>
      </UiButton>
    </div>

    <UiAlert variant="error" :message="error || ''" dismissible @dismiss="clientStore.error = null" />

    <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
      <div>
        <label class="label">Nome</label>
        <input v-model="filters.name" type="text" placeholder="Filtrar por nome..." class="input" />
      </div>
      <div>
        <label class="label">Email</label>
        <input v-model="filters.email" type="text" placeholder="Filtrar por email..." class="input" />
      </div>
      <div>
        <label class="label">Telefone</label>
        <input v-model="filters.phone" type="text" placeholder="Filtrar por telefone..." class="input" />
      </div>
      <div class="flex items-end sm:col-span-3">
        <UiButton variant="secondary" size="sm" @click="clearFilters">
          Limpar Filtros
        </UiButton>
      </div>
    </div>

    <UiCard padding>
      <UiLoader v-if="isLoading && !clients.length" text="Carregando clientes..." />

      <div v-else-if="filteredClients.length === 0 && !error" class="flex flex-col items-center py-16 text-center">
        <div class="mb-4 rounded-full bg-surface-100 dark:bg-surface-800 p-4">
          <svg class="h-8 w-8 text-surface-400 dark:text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-surface-700 dark:text-surface-200">Nenhum cliente cadastrado</h3>
        <p class="mt-1 text-sm text-surface-400 dark:text-surface-500">Clique em "Novo Cliente" para começar</p>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th class="hidden sm:table-cell">Email</th>
              <th class="hidden sm:table-cell">Telefone</th>
              <th class="w-24 sm:w-40">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="client in filteredClients" :key="client.id">
              <td>
                <div class="font-medium text-surface-800 dark:text-surface-100">{{ client.name }}</div>
                <!-- Show email and phone inline on mobile -->
                <div class="mt-1 space-y-0.5 sm:hidden">
                  <div v-if="client.email" class="text-xs text-surface-400 dark:text-surface-500">{{ client.email }}</div>
                  <div v-if="client.phone" class="text-xs text-surface-400 dark:text-surface-500">{{ formatPhone(client.phone) }}</div>
                </div>
              </td>
              <td class="hidden sm:table-cell">
                <span v-if="client.email" class="text-surface-500 dark:text-surface-400">{{ client.email }}</span>
                <span v-else class="text-surface-300 italic dark:text-surface-600">—</span>
              </td>
              <td class="hidden sm:table-cell">
                <span v-if="client.phone" class="text-surface-500 dark:text-surface-400">{{ formatPhone(client.phone) }}</span>
                <span v-else class="text-surface-300 italic dark:text-surface-600">—</span>
              </td>
              <td>
                <div class="flex items-center gap-1 sm:gap-2">
                  <UiButton variant="ghost" size="sm" @click="openEdit(client)">
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span class="hidden sm:inline">Editar</span>
                  </UiButton>
                  <UiButton variant="ghost" size="sm" @click="confirmDelete(client.id)">
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span class="hidden sm:inline">Excluir</span>
                  </UiButton>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </UiCard>

    <UiModal :open="showModal" :title="isEditing ? 'Editar Cliente' : 'Novo Cliente'" @close="closeModal">
      <form @submit.prevent="handleSave" class="space-y-5">
        <UiInput
          v-model="form.name"
          label="Nome"
          placeholder="Nome completo"
          required
          :error="formErrors.name"
        />
        <UiInput
          v-model="form.email"
          label="Email"
          type="email"
          placeholder="email@exemplo.com"
          :error="formErrors.email"
        />
        <UiInput
          v-model="form.phone"
          label="Telefone"
          type="tel"
          placeholder="(11) 99999-9999"
        />
      </form>
      <template #footer>
        <UiButton variant="secondary" @click="closeModal">Cancelar</UiButton>
        <UiButton variant="primary" :loading="saving" @click="handleSave">
          {{ isEditing ? 'Salvar Alterações' : 'Adicionar Cliente' }}
        </UiButton>
      </template>
    </UiModal>

    <UiModal
      :open="!!deleteConfirmId"
      title="Excluir Cliente"
      max-width="max-w-sm"
      @close="deleteConfirmId = null"
    >
      <p class="text-sm text-surface-600 dark:text-surface-300">
        Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.
      </p>
      <template #footer>
        <UiButton variant="secondary" @click="deleteConfirmId = null">Cancelar</UiButton>
        <UiButton variant="danger" :loading="isLoading" @click="handleDelete">
          Excluir
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>
